import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Button, Checkbox, createStyles, Group, List, Modal, Stack, Text} from '@mantine/core';
import React, {useState} from 'react';

import {RoutineStatus} from 'llm/routines/RoutineTypes';
import {getUserMessage} from 'util/Net';

import {AgentFoundryQuery_agentFoundry_AgentFoundryNode_coordinatorAgent_RoutineNode as FoundryRoutine} from './__generated/AgentFoundryQuery';

// The theme centers the checkbox label and box, which misaligns them once a description is shown.
const useStyles = createStyles({
  checkboxLabel: {alignSelf: 'flex-start'},
  checkboxInner: {alignSelf: 'flex-start'},
});

export type AgentArchiveOutcome = 'ARCHIVE' | 'PAUSE' | 'SKIP' | 'NONE';

/**
 * What archiving a foundry will do to one of its agents, mirroring `archive_agent_foundry` on the
 * server: archive it if asked and permitted, otherwise pause it if it is running and pausable,
 * otherwise leave it alone. 'SKIP' marks an agent the user wanted changed but lacks permission to
 * change, including one they cannot view at all (a null routine).
 */
export function getAgentArchiveOutcome(
  routine: FoundryRoutine | null,
  archiveAgents: boolean
): AgentArchiveOutcome {
  if (routine == null) {
    return 'SKIP';
  }
  if (routine.archiveData.archived) {
    return 'NONE';
  }
  const isRunning = routine.status === RoutineStatus.ACTIVE;
  if (archiveAgents && routine.permissions.archivable === true) {
    return 'ARCHIVE';
  }
  if (isRunning && routine.permissions.writable) {
    return 'PAUSE';
  }
  return isRunning || archiveAgents ? 'SKIP' : 'NONE';
}

function SkippedAgentsAlert({
  skippedAgents,
  archiveAgents,
}: {
  skippedAgents: Array<FoundryRoutine | null>;
  archiveAgents: boolean;
}): JSX.Element {
  const viewableAgents = skippedAgents.filter((routine): routine is FoundryRoutine => routine != null);
  const hiddenAgentCount = skippedAgents.length - viewableAgents.length;
  return (
    <Alert
      color="yellow"
      title="Some agents won't be changed"
      {...getTestProps({component: 'ArchiveAgentFoundryModal', element: 'skippedAgentsAlert'})}
    >
      <Stack spacing="xs">
        <Text size="sm">
          {archiveAgents
            ? "You don't have permission to archive or pause these agents, so they'll keep their current state:"
            : "You don't have permission to pause these agents, so they'll keep running:"}
        </Text>
        <List size="sm">
          {viewableAgents.map((routine) => (
            <List.Item key={routine.id}>{routine.name}</List.Item>
          ))}
          {hiddenAgentCount > 0 && (
            <List.Item>
              {hiddenAgentCount === 1 ? '1 agent' : `${hiddenAgentCount} agents`} you don't have access to
            </List.Item>
          )}
        </List>
        <Text size="sm">Ask the owners of these agents to stop them if they're no longer needed.</Text>
      </Stack>
    </Alert>
  );
}

interface ArchiveAgentFoundryModalProps {
  opened: boolean;
  foundryName: string;
  // The foundry's Coordinator and agents. A null entry is an agent the user cannot view.
  agents: Array<FoundryRoutine | null>;
  onRequestClose: () => void;
  onRequestSubmit: (options: {archiveAgents: boolean}) => Promise<void>;
}

/**
 * Confirms archiving an Agent Foundry. Archiving always pauses the foundry's running agents;
 * the user can opt into archiving the agents too. Foundry permissions don't extend to its agents,
 * so any agent the user can't pause or archive is listed up front as one that won't be changed.
 */
export default function ArchiveAgentFoundryModal({
  opened,
  foundryName,
  agents,
  onRequestClose,
  onRequestSubmit,
}: ArchiveAgentFoundryModalProps): JSX.Element {
  const {classes} = useStyles();
  const [archiveAgents, setArchiveAgents] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const skippedAgents = agents.filter((routine) => getAgentArchiveOutcome(routine, archiveAgents) === 'SKIP');

  async function handleSubmit(): Promise<void> {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await onRequestSubmit({archiveAgents});
      onRequestClose();
    } catch (err: unknown) {
      const serverMessage = getUserMessage(err);
      if (serverMessage) {
        setErrorMessage(serverMessage);
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        throw err;
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onRequestClose} title="Archive foundry" size="md" centered={true}>
      <Stack spacing="md">
        {errorMessage != null && (
          <Text role="alert" size="sm" c="red">
            {errorMessage}
          </Text>
        )}
        <Text size="sm">
          Archiving <b>{foundryName}</b> removes it from your foundries. Its running agents will be paused.
        </Text>
        {agents.length > 0 && (
          <Checkbox
            label="Also archive this foundry's agents"
            description="Archived agents stop running and are removed from your agents."
            classNames={{label: classes.checkboxLabel, inner: classes.checkboxInner}}
            checked={archiveAgents}
            onChange={(event) => {
              setArchiveAgents(event.currentTarget.checked);
            }}
            {...getTestProps({component: 'ArchiveAgentFoundryModal', element: 'archiveAgentsCheckbox'})}
          />
        )}
        {skippedAgents.length > 0 && (
          <SkippedAgentsAlert skippedAgents={skippedAgents} archiveAgents={archiveAgents} />
        )}
        <Group position="right" spacing="xs">
          <Button
            {...getTestProps({component: 'ArchiveAgentFoundryModal', element: 'cancelButton'})}
            variant="tertiary"
            onClick={onRequestClose}
          >
            Cancel
          </Button>
          <Button
            variant="warn"
            loading={isSubmitting}
            onClick={handleSubmit}
            {...getTestProps({component: 'ArchiveAgentFoundryModal', element: 'submitButton'})}
          >
            Archive
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
